import React, { useEffect, useMemo, useRef, useState } from 'react';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAuth } from '../contexts/AuthContext';
import './Signature.css';

const DEFAULTS = {
  fullName: 'John Robinson',
  title: 'Director',
  department: 'Solutions Delivery',
  company: 'RSC',
  phone1: '551-800-4900',
  phone2: '',
  email: 'john.robinson@rsc.com',
  address1: '22 Paris Ave',
  address2: 'Suite 201',
  city: 'Rockleigh',
  state: 'NJ',
  zip: '07647',
  country: ''
};

const COMPANIES = {
  RSC: {
    site: 'www.rsc.com',
    logo: 'https://rscwebsitestorage.blob.core.windows.net/$web/RSC Base Logo_1024x600_NEW.png',
    logoWidth: 165
  },
  'RSC Solutions': {
    site: 'www.rsc.com/solutions',
    logo: 'https://rscwebsitestorage.blob.core.windows.net/$web/RSC Solutions Logo_1024x600_NEW.png',
    logoWidth: 165
  },
  'RSC Energy': {
    site: 'www.rsc.com/energy',
    logo: 'https://rscwebsitestorage.blob.core.windows.net/$web/RSC Energy Logo_1024x600_NEW.png',
    logoWidth: 165
  },
  'RSC Healthcare': {
    site: 'www.rsc.com/healthcare',
    logo: 'https://rscwebsitestorage.blob.core.windows.net/$web/RSC Healthcare Logo_1024x600_NEW.png',
    logoWidth: 165
  },
  'RSC Maritime': {
    site: 'www.rsc.com/maritime',
    logo: 'https://rscwebsitestorage.blob.core.windows.net/$web/RSC Maritime Logo_1024x600_NEW.png',
    logoWidth: 165
  }
};

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const getTenDigitPhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.length > 10 ? digits.slice(-10) : digits;
};

const formatUsPhone = (value) => {
  const digits = getTenDigitPhone(value);
  if (digits.length !== 10) return value ? String(value).trim() : '';
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const buildAddressHtml = (form) => {
  const addr1 = (form.address1 || '').trim();
  const addr2 = (form.address2 || '').trim();
  const city = (form.city || '').trim();
  const state = (form.state || '').trim();
  const zip = (form.zip || '').trim();
  const country = (form.country || '').trim();

  const line1 = [addr1, addr2].filter(Boolean).map(escapeHtml).join(', ');
  const cityState = [city, state].filter(Boolean).map(escapeHtml).join(', ');
  const line2 = zip ? [cityState, escapeHtml(zip)].filter(Boolean).join(' ') : cityState;

  return [line1, line2, country ? escapeHtml(country) : ''].filter(Boolean).join('<br>');
};

const buildSignatureHtml = (form) => {
  const companyInfo = COMPANIES[form.company] || COMPANIES.RSC;
  const fullName = escapeHtml(form.fullName);
  const title = escapeHtml(form.title);
  const company = escapeHtml(form.company);
  const department = (form.department || '').trim() ? escapeHtml(form.department) : '';
  const deptComp = department ? `${department}&nbsp;|&nbsp;${company}` : company;
  const formattedPhone1 = formatUsPhone(form.phone1);
  const formattedPhone2 = formatUsPhone(form.phone2);
  const phone1 = escapeHtml(formattedPhone1);
  const phone2 = escapeHtml(formattedPhone2);
  const phone1Tel = getTenDigitPhone(form.phone1);
  const phone2Tel = getTenDigitPhone(form.phone2);
  const email = escapeHtml(form.email);
  const site = escapeHtml(companyInfo.site);
  const siteHref = /^https?:\/\//i.test(companyInfo.site || '')
    ? companyInfo.site
    : `https://${companyInfo.site || ''}`;
  const logo = escapeHtml(companyInfo.logo);
  const logoWidth = Number(companyInfo.logoWidth) || 130;
  const addressHtml = buildAddressHtml(form);

  const phoneSection = phone1Tel && phone2Tel
    ? `<a href="tel:${phone1Tel}" style="text-decoration:none; color:#000; font-size:14px;">${phone1}</a>&nbsp;|&nbsp;<a href="tel:${phone2Tel}" style="text-decoration:none; color:#000; font-size:14px;">${phone2}</a>`
    : phone1Tel
      ? `<a href="tel:${phone1Tel}" style="text-decoration:none; color:#000; font-size:14px;">${phone1}</a>`
      : phone2Tel
        ? `<a href="tel:${phone2Tel}" style="text-decoration:none; color:#000; font-size:14px;">${phone2}</a>`
        : '';

  return `
<table cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:560px; font-family:Arial, sans-serif; font-size:14px; color:#000;">
  <tr>
    <td>
      <table cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          <td width="240" valign="top" style="font-family:Arial, sans-serif;">
            <span style="font-size:18px; font-weight:600; line-height:1.4; display:block;">${fullName}</span>
            <span style="font-size:14px; line-height:1.4; display:block; margin-top:6px;">${title}</span>
            <span style="font-size:14px; line-height:1.4; font-weight:500; display:block; margin-top:6px;">${deptComp}</span>
          </td>
          <td width="20"></td>
          <td width="10" align="center" valign="middle">
            <table cellpadding="0" cellspacing="0" border="0" style="height:122px;">
              <tr>
                <td width="1" style="border-left:1px solid rgb(239,157,35); height:122px; line-height:122px;">&nbsp;</td>
              </tr>
            </table>
          </td>
          <td width="16"></td>
          <td valign="top" style="font-family:Arial, sans-serif; font-size:14px; line-height:1.3;">
            <table cellpadding="0" cellspacing="0" border="0">
              <tr height="28">
                <td width="26" valign="middle">
                  <img src="https://rscwebsitestorage.blob.core.windows.net/$web/phone_orange.png" width="18" alt="phone" style="display:block;">
                </td>
                <td valign="middle" style="white-space:nowrap;">${phoneSection || '&nbsp;'}</td>
              </tr>
              <tr height="28">
                <td width="26" valign="middle">
                  <img src="https://rscwebsitestorage.blob.core.windows.net/$web/mail_orange.png" width="18" alt="email" style="display:block;">
                </td>
                <td valign="middle">
                  <a href="mailto:${email}" style="text-decoration:none; color:#000;">${email}</a>
                </td>
              </tr>
              <tr height="28">
                <td width="26" valign="middle">
                  <img src="https://rscwebsitestorage.blob.core.windows.net/$web/globe_orange.png" width="18" alt="website" style="display:block;">
                </td>
                <td valign="middle">
                  <a href="${escapeHtml(siteHref)}" style="text-decoration:none; color:#000;">${site}</a>
                </td>
              </tr>
              <tr height="28">
                <td width="26" valign="middle">
                  <img src="https://rscwebsitestorage.blob.core.windows.net/$web/map_orange.png" width="18" alt="address" style="display:block;">
                </td>
                <td valign="middle">
                  <span style="color:#000;">${addressHtml}</span>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td>
      <table cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr><td height="4"></td></tr>
        <tr>
          <td style="border-bottom:1px solid rgb(239,157,35); height:1px; width:100%;">&nbsp;</td>
        </tr>
        <tr><td height="3"></td></tr>
      </table>
    </td>
  </tr>
  <tr>
    <td>
      <img src="${logo}" width="${logoWidth}" alt="RSC" style="display:block; border:0; height:auto;">
    </td>
  </tr>
</table>`;
};

const buildFormDataFromUser = (base, user) => {
  if (!user) return base;

  const businessPhones = Array.isArray(user.businessPhones) ? user.businessPhones.filter(Boolean) : [];
  const mobilePhone = (user.mobilePhone || '').trim();
  const primaryPhone = businessPhones[0] || mobilePhone || base.phone1;
  const secondaryPhone = mobilePhone && mobilePhone !== primaryPhone
    ? mobilePhone
    : (businessPhones[1] || base.phone2);

  let company = base.company;
  const graphCompany = (user.companyName || '').trim();
  if (graphCompany && Object.prototype.hasOwnProperty.call(COMPANIES, graphCompany)) {
    company = graphCompany;
  }

  return {
    ...base,
    fullName: (user.displayName || '').trim() || base.fullName,
    title: (user.jobTitle || '').trim() || base.title,
    department: (user.department || '').trim() || base.department,
    company,
    email: (user.mail || user.userPrincipalName || '').trim() || base.email,
    phone1: formatUsPhone(primaryPhone),
    phone2: formatUsPhone(secondaryPhone),
    address1: (user.streetAddress || '').trim() || base.address1,
    city: (user.city || '').trim() || base.city,
    state: (user.state || '').trim() || base.state,
    zip: (user.postalCode || '').trim() || base.zip,
    country: (user.country || '').trim() || base.country
  };
};

const Signature = () => {
  const { user } = useAuth();
  const previewFrameRef = useRef(null);
  const [formData, setFormData] = useState(DEFAULTS);
  const [baselineFormData, setBaselineFormData] = useState(DEFAULTS);
  const [profilePrefilled, setProfilePrefilled] = useState(false);
  const [copyState, setCopyState] = useState('Copy HTML');

  const adjustPreviewHeight = () => {
    const frame = previewFrameRef.current;
    if (!frame) return;

    try {
      const doc = frame.contentDocument || frame.contentWindow?.document;
      if (!doc || !doc.body || !doc.documentElement) return;

      frame.style.height = '0px';
      const height = Math.max(
        doc.body.scrollHeight,
        doc.body.offsetHeight,
        doc.documentElement.scrollHeight,
        doc.documentElement.offsetHeight
      );
      frame.style.height = `${height}px`;
    } catch {
      // no-op
    }
  };

  useEffect(() => {
    if (!user || profilePrefilled) return;
    const prefilled = buildFormDataFromUser(DEFAULTS, user);
    setFormData(prefilled);
    setBaselineFormData(prefilled);
    setProfilePrefilled(true);
  }, [user, profilePrefilled]);

  useEffect(() => {
    const onResize = () => adjustPreviewHeight();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const signatureHtml = useMemo(() => buildSignatureHtml(formData), [formData]);

  const onChange = (event) => {
    const { name, value } = event.target;
    if (name === 'phone1' || name === 'phone2') {
      setFormData((prev) => ({ ...prev, [name]: formatUsPhone(value) }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onReset = () => {
    setFormData(baselineFormData);
  };

  const onCopy = async () => {
    try {
      const previewSrcDoc = previewFrameRef.current?.srcdoc || previewFrameRef.current?.getAttribute('srcdoc');
      const previewHtml = (previewSrcDoc || signatureHtml).trim();

      if (navigator.clipboard && window.ClipboardItem) {
        const htmlBlob = new Blob([previewHtml], { type: 'text/html' });
        const textBlob = new Blob([previewHtml], { type: 'text/plain' });
        const clipboardItem = new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob
        });
        await navigator.clipboard.write([clipboardItem]);
      } else {
        await navigator.clipboard.writeText(previewHtml);
      }
      setCopyState('Copied');
      setTimeout(() => setCopyState('Copy HTML'), 1400);
    } catch (error) {
      setCopyState('Copy failed');
      setTimeout(() => setCopyState('Copy HTML'), 1800);
    }
  };

  return (
    <div className="container signature-page">
      <div className="card">
        <div className="signature-header">
          <h1>Signature Builder</h1>
          <p>Create and copy your Outlook-ready email signature.</p>
        </div>

        <div className="signature-instructions">
          <strong>How to use this form</strong>
          <ol>
            <li>Fill out the form fields. The preview updates automatically.</li>
            <li>Click “Copy HTML” to copy your signature markup.</li>
            <li>Paste it into Outlook signature settings and save.</li>
          </ol>
        </div>

        <div className="signature-form-grid">
          <label>Full name<input name="fullName" value={formData.fullName} onChange={onChange} /></label>
          <label>Title<input name="title" value={formData.title} onChange={onChange} /></label>
          <label>Department / Line<input name="department" value={formData.department} onChange={onChange} /></label>
          <label>
            Company
            <select name="company" value={formData.company} onChange={onChange}>
              {Object.keys(COMPANIES).map((company) => (
                <option key={company} value={company}>{company}</option>
              ))}
            </select>
          </label>
          <label>Phone 1<input name="phone1" value={formData.phone1} onChange={onChange} /></label>
          <label>Phone 2<input name="phone2" value={formData.phone2} onChange={onChange} /></label>
          <label>Email<input name="email" value={formData.email} onChange={onChange} /></label>
          <label>Address 1<input name="address1" value={formData.address1} onChange={onChange} /></label>
          <label>Address 2<input name="address2" value={formData.address2} onChange={onChange} /></label>
          <label>City<input name="city" value={formData.city} onChange={onChange} /></label>
          <label>State<input name="state" value={formData.state} onChange={onChange} /></label>
          <label>Zip<input name="zip" value={formData.zip} onChange={onChange} /></label>
          <label>Country<input name="country" value={formData.country} onChange={onChange} /></label>
        </div>

        <div className="signature-actions">
          <button type="button" className="btn btn-primary" onClick={onCopy}>{copyState}</button>
          <button type="button" className="btn btn-secondary" onClick={onReset}>Reset</button>
        </div>

        <div className="signature-preview-wrap">
          <iframe
            title="Signature Preview"
            srcDoc={signatureHtml}
            className="signature-preview-frame"
            ref={previewFrameRef}
            onLoad={adjustPreviewHeight}
          />
        </div>
      </div>
    </div>
  );
};

const SignatureWithProtection = () => (
  <ProtectedRoute requiredTab="signature-builder">
    <Signature />
  </ProtectedRoute>
);

export default SignatureWithProtection;
